/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { request } from '@/api/client';

/**
 * Search across the estate.
 *
 * Asked of the server, not computed here. A search over what the browser
 * happens to hold only ever finds the first page of the list — and the thing
 * somebody is looking for is usually the thing they could not find by
 * scrolling.
 */
export const hitSchema = z.object({
  kind: z.enum(['agreement', 'workflow', 'counterparty', 'packet', 'person']),
  id: z.string(),
  title: z.string(),
  detail: z.string(),
});

export type Hit = z.infer<typeof hitSchema>;

export const searchSchema = z.object({
  hits: z.array(hitSchema),
  truncated: z.boolean(),
  per_kind: z.number(),
  min_term: z.number(),
});

export type SearchResults = z.infer<typeof searchSchema>;

/** How each kind reads, and which icon stands for it. */
export const KIND_LABEL: Record<Hit['kind'], string> = {
  agreement: 'Agreement',
  workflow: 'Workflow',
  counterparty: 'Counterparty',
  packet: 'Packet',
  person: 'Person',
};

export const searchQuery = (term: string) =>
  queryOptions({
    queryKey: ['search', term] as const,
    queryFn: ({ signal }) =>
      request(`/api/v1/search?q=${encodeURIComponent(term)}`, searchSchema, { signal }),
    // Results for a term do not change while somebody is still typing it.
    staleTime: 30_000,
    enabled: term.trim().length >= 2,
  });
